import * as React from 'react';
import type { BarriosStats } from '../../types/barrios-landing';

interface SocialProofProps {
  stats: BarriosStats;
}

function formatNumber(n: number): string {
  if (n >= 1000) {
    return n.toLocaleString('es-AR');
  }
  return String(n);
}

export function SocialProof({ stats }: SocialProofProps) {
  if (stats.totalOrders === 0 && stats.topProducts.length === 0) {
    return null;
  }

  return (
    <section className="bp-social-proof">
      <style>{`
        .bp-social-proof {
          padding: 64px 24px;
          background: #fafaf7;
        }
        .bp-social-inner {
          max-width: 960px;
          margin: 0 auto;
        }
        .bp-social-proof h2 {
          text-align: center;
          font-size: 26px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 12px;
        }
        .bp-social-proof .section-sub {
          text-align: center;
          font-size: 16px;
          color: #666;
          margin: 0 0 40px;
        }
        .bp-stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-bottom: 40px;
        }
        .bp-stat-card {
          text-align: center;
          padding: 24px 16px;
          background: #fff;
          border-radius: 12px;
          border: 1px solid #eee;
        }
        .bp-stat-number {
          font-size: 32px;
          font-weight: 800;
          color: #1EAA25;
          line-height: 1;
          margin-bottom: 6px;
        }
        .bp-stat-label {
          font-size: 14px;
          color: #666;
        }
        .bp-top-products {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        .bp-top-product {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          background: #fff;
          border-radius: 10px;
          border: 1px solid #eee;
        }
        .bp-top-product img {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
        }
        .bp-top-product-info {
          min-width: 0;
        }
        .bp-top-product-name {
          font-size: 14px;
          font-weight: 600;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .bp-top-product-stat {
          font-size: 13px;
          color: #1EAA25;
          font-weight: 500;
        }
        @media (max-width: 768px) {
          .bp-stats-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
          }
          .bp-stat-number {
            font-size: 24px;
          }
          .bp-top-products {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      <div className="bp-social-inner">
        <h2>En los últimos 30 días</h2>
        <p className="section-sub">Entregamos en barrios privados y countries de Córdoba todos los días.</p>

        <div className="bp-stats-grid">
          <div className="bp-stat-card">
            <div className="bp-stat-number">{formatNumber(stats.totalKilos)}</div>
            <div className="bp-stat-label">kilos entregados en countries</div>
          </div>
          <div className="bp-stat-card">
            <div className="bp-stat-number">{formatNumber(stats.totalOrders)}</div>
            <div className="bp-stat-label">pedidos en barrios privados</div>
          </div>
          <div className="bp-stat-card">
            <div className="bp-stat-number">{formatNumber(stats.totalCountries)}</div>
            <div className="bp-stat-label">barrios alcanzados</div>
          </div>
        </div>

        {stats.topProducts.length > 0 && (
          <div className="bp-top-products">
            {stats.topProducts.map((p) => (
              <div key={p.product_id} className="bp-top-product">
                {p.image_url && (
                  <img src={p.image_url} alt={p.product_name} loading="lazy" />
                )}
                <div className="bp-top-product-info">
                  <div className="bp-top-product-name">{p.product_name}</div>
                  <div className="bp-top-product-stat">
                    {formatNumber(p.total_units)} {p.unit_short_name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
